import { useState, useEffect, useCallback, useRef, ReactNode } from 'react';
import {
  InputGroup,
  Button,
  Overlay,
  Tooltip,
  ButtonGroup,
  Form,
  Col,
  Row,
  OverlayTrigger,
  Popover,
} from 'react-bootstrap';

import AddressDetails from './AddressDetails';

import AddressTypeahead from 'components/AddressTypeahead';
import { IAdresse } from 'components/AddressTypeahead/types';
import { IcebergIcon, IconButton, IcebergButton } from '@tradesolution/iceberg-ui-react';

import PostnummerRegisterApi from 'services/KjederegisteretAdminApi/PostnummerRegisterApi';
import { KontonrOgFakturaAdresseSjekkResponse } from 'services/KjederegisteretAdminApi/EnhetApi/types';
import AdresseApi from 'services/KjederegisteretAdminApi/AdresseApi';
import { EnhetAtAdresseDto } from 'services/KjederegisteretAdminApi/AdresseApi/types';

import { formatAddressAsOneLiner } from 'utils/AddressHelper';
import useIsMountedRef from 'utils/hooks/useIsMountedRef';

import Style from './index.module.css';
import PopoverMenuAdressInput from './PopoverMenuAdressInput';
import { Coordinates } from 'models/Coordinates';

interface Props {
  title: ReactNode;
  adresse: IAdresse;
  onAdresseChanged: (adresse: IAdresse) => void;
  onAdresseCopied: (adresse: IAdresse, ekstraInfo: string) => void;
  ekstraInfo: string;
  onEkstraInfoChanged: (info: string) => void;
  copyAddresses?: { adresse: IAdresse; title: string; ekstraInfo: string }[];
  kontonrOgFakturaAdresseSjekkResponse?: KontonrOgFakturaAdresseSjekkResponse;
  requireCoordinates?: boolean;
  onClear: () => void;
  onDelete: () => void;
  onSave: () => void;
  saveDisabledDueToPendingRemoval?: boolean;
  resetTouchedState?: boolean;
  onUpdateCoordinates: (coor: { lat: number; lng: number }) => void;
  onDeleteCoordinates: (id: number) => void;
  coordinates: Coordinates;
  setCoordinates: (coordinates: Coordinates) => void;
}

const AdresseInput = (props: Props) => {
  const isMountedRef = useIsMountedRef();

  const adresseInputRef = useRef(null);

  const [touched, setTouched] = useState<boolean>(false);
  const [errors, setErrors] = useState({} as any);
  const [showDetails, setShowDetails] = useState<boolean>(false);
  const [isResetDisabled, setIsResetDisabled] = useState<boolean>(true);
  const [showReadonlyWarning, setShowReadonlyWarning] = useState<boolean>(false);
  const [activeEnheterOnAddress, setActiveEnheterOnAddress] = useState<EnhetAtAdresseDto[]>([]);

  // Fetch active enheter at the address
  useEffect(() => {
    const fetchActiveEnheter = async (gateadresse: string, postnr: string) => {
      try {
        const enheter = await AdresseApi.getActiveEnheter(gateadresse, postnr);
        if (isMountedRef.current) {
          setActiveEnheterOnAddress(enheter);
        }
      } catch (error) {
        console.error('Error fetching active enheter:', error);
      }
    };

    if (props.adresse?.gateadresse && props.adresse?.postnr && isMountedRef.current) {
      fetchActiveEnheter(props.adresse.gateadresse, props.adresse.postnr);
    } else {
      setActiveEnheterOnAddress([]);
    }
  }, [props.adresse?.gateadresse, props.adresse?.postnr, isMountedRef]);

  const handlePostnrChanged = async (postnr: string) => {
    const addressCopy = { ...props.adresse };
    if (postnr && postnr.length === 4) {
      const info = await PostnummerRegisterApi.getByPostkode(postnr);
      if (info) {
        addressCopy['postnr'] = postnr;
        addressCopy['poststed'] = info.poststed;
      } else {
        addressCopy['postnr'] = postnr;
        addressCopy['poststed'] = null;
      }
    } else {
      addressCopy['postnr'] = postnr;
    }
    addressCopy.formattedForOneLine = formatAddressAsOneLiner(addressCopy);
    setTouched(true);
    props.onAdresseChanged(addressCopy);
  };

  // update specific address object value, ex: besoksadresse.postnr
  const handleAddressValuesChanged = (targetName: string, value: string) => {
    if (targetName) {
      const addressCopy = { ...props.adresse };
      addressCopy[targetName] = value;
      addressCopy.formattedForOneLine = formatAddressAsOneLiner(addressCopy);
      props.onAdresseChanged(addressCopy);
      setTouched(true);
    }
  };

  const validate = useCallback(() => {
    let adresseErrors: any = {};

    if (props.adresse) {
      const isPostnummerOnlyNumbers = /^\d+$/.test(props.adresse?.postnr);
      const hasPoststedNumbers = /\d/.test(props.adresse?.poststed);
      if (!props.adresse.gateadresse) adresseErrors.gateadresse = 'Gatenavn mangler!';
      if (!isPostnummerOnlyNumbers) adresseErrors.postnr = 'Bare tall i postnummer!';
      if (!props.adresse.postnr) adresseErrors.postnr = 'Postnummer mangler!';
      if (props.adresse.postnr?.length > 4) adresseErrors.postnr = 'Kun 4 tall i postnummer!';
      if (!props.adresse.poststed) adresseErrors.poststed = 'Poststed mangler!';
      if (hasPoststedNumbers) adresseErrors.poststed = 'Poststed skal ikke inneholde tall';
      if (!props.adresse.landkode) adresseErrors.landkode = 'Land mangler!';
      if (props.requireCoordinates && props.requireCoordinates === true) {
        if (!props.adresse.latitude || !props.adresse.longitude) {
          adresseErrors.koordinater = 'Koordinater mangler!';
        }
      }
    }
    setErrors(adresseErrors);
  }, [props.adresse]);

  useEffect(() => {
    if (isMountedRef.current) {
      validate();
    }
  }, [isMountedRef, props.adresse, validate]);

  const hasErrors = errors && Object.keys(errors).find((key) => errors[key])?.length > 0;

  const disableAddressInput =
    props.kontonrOgFakturaAdresseSjekkResponse?.harSammeOrgNr &&
    !props.kontonrOgFakturaAdresseSjekkResponse.fakturaAdresseErForskjellig;
  const hasAddressChangedRef = useRef(false);
  const saveDisabled = (!hasErrors && !touched) || props.saveDisabledDueToPendingRemoval;

  useEffect(() => {
    if (hasAddressChangedRef.current) {
      props.onSave();
      hasAddressChangedRef.current = false;
    }
  }, [props.adresse, props.onSave]);

  const handleAddressChanged = (newAddress: IAdresse) => {
    setTouched(true);
    props.onAdresseChanged(newAddress);
  };

  const handleSaveClicked = () => {
    props.onSave();
    setTouched(false);
  };

  // Reset touched state when parent signals save was completed (e.g., from "Bruk..." button)
  useEffect(() => {
    if (props.resetTouchedState) {
      setTouched(false);
    }
  }, [props.resetTouchedState]);

  const formatErrors = (errors: object): ReactNode => {
    return Object.entries(errors).map((o) => <li key={o[0]}>{o[1]}</li>);
  };

  const handleEkstraInfoChanged = (info: string): void => {
    setTouched(true);
    props.onEkstraInfoChanged(info);
  };

  return (
    <div className={Style.addressContainer}>
      <h6>{props.title}</h6>
      {props.ekstraInfo && (
        <div>
          <span>Ekstra info: </span>
          <span className="text-muted">{props.ekstraInfo}</span>
        </div>
      )}
      <Row className="align-items-start g-2">
        <Col xs={12} md={8} className="mb-2 mb-md-0">
          <InputGroup
            size="sm"
            ref={adresseInputRef}
            onFocus={() => setShowReadonlyWarning((prev) => (disableAddressInput ? !prev : false))}
            onBlur={() => setShowReadonlyWarning(false)}
          >
            {!props.adresse ? (
              <InputGroup.Text style={{ paddingLeft: '.6rem', paddingRight: '.6rem' }}>
                <IcebergIcon icon="search" />
              </InputGroup.Text>
            ) : (
              <InputGroup.Text style={{ paddingLeft: '.45rem', paddingRight: '.45rem' }}>
                <IcebergIcon icon="check" />
              </InputGroup.Text>
            )}
            <AddressTypeahead
              size="sm"
              allowNew
              selected={props.adresse}
              onChange={handleAddressChanged}
              isInvalid={props.adresse?.formattedForOneLine.length > 0 && hasErrors}
              isValid={touched && props.adresse?.formattedForOneLine.length > 0 && !hasErrors}
              disabled={disableAddressInput}
              handleDelete={props.onClear}
            />
          </InputGroup>
        </Col>
        <Col className="d-flex gap-2">
          <IcebergButton
            size="sm"
            icon={showDetails ? 'eye-closed' : 'eye-open'}
            variant="outline-primary"
            onClick={() => {
              setShowDetails((prev) => !prev);
              setIsResetDisabled(true);
              setShowReadonlyWarning(false);
            }}
          >
            Detaljer
          </IcebergButton>
          <IcebergButton
            size="sm"
            disabled={saveDisabled}
            icon="disk"
            variant="primary"
            onClick={handleSaveClicked}
          >
            Lagre
          </IcebergButton>
        </Col>
      </Row>

      {/* <PopoverMenuAdressInput onDelete={props.onDelete} /> */}

      <Overlay
        rootClose
        target={adresseInputRef.current}
        show={showReadonlyWarning}
        placement="top"
      >
        {(props) => (
          <Tooltip id="overlay-warning" className={Style.adresseTooltip} {...props}>
            Enhet har samme orgnr som kjedens hovedkontor og skal da dele fakturaadresse. Dette kan
            ikke endres.
          </Tooltip>
        )}
      </Overlay>

      {hasErrors && (
        <Form.Text style={{ color: 'red' }}>
          <ul>{formatErrors(errors)}</ul>
        </Form.Text>
      )}

      {activeEnheterOnAddress?.length > 0 && (
        <OverlayTrigger
          trigger="click"
          placement="bottom"
          rootClose
          overlay={
            <Popover id="active-enheter-popover">
              <Popover.Header as="h3">Aktive enheter på adressen</Popover.Header>
              <Popover.Body>
                <ul style={{ paddingLeft: '1rem', marginBottom: 0 }}>
                  {[...activeEnheterOnAddress]
                    .sort((a, b) => (a.navn || '').localeCompare(b.navn || ''))
                    .map((e) => (
                      <li key={`${e.enhetId}-${e.lopenummer ?? 'x'}`}>
                        <strong>
                          <a href={`/enheter/${e.enhetId}`} target="_blank" rel="noreferrer">
                            {e.lopenummer} - {e.navn || 'Ukjent navn'}
                          </a>
                        </strong>
                        {e.hovedbransje ? ` – ${e.hovedbransje}` : ''}
                      </li>
                    ))}
                </ul>
              </Popover.Body>
            </Popover>
          }
        >
          <Button
            variant="link"
            className="p-0 text-muted"
            style={{ textDecoration: 'underline', cursor: 'pointer' }}
          >
            Merk: Det finnes {activeEnheterOnAddress.length} aktive enheter på denne adressen.
          </Button>
        </OverlayTrigger>
      )}

      {!disableAddressInput && (
        <div className={Style.optionsContainer}>
          <div className={Style.addressOptions}>
            {props.copyAddresses &&
              props.copyAddresses.length > 0 &&
              props.copyAddresses.map((copyAddress) => (
                <Button
                  key={copyAddress.title}
                  size="sm"
                  variant="outline-secondary"
                  className="me-1 mt-2"
                  disabled={
                    !copyAddress.adresse?.id ||
                    showDetails ||
                    copyAddress.adresse.formattedForOneLine === props.adresse?.formattedForOneLine
                  }
                  onClick={() => {
                    props.onAdresseCopied(copyAddress.adresse, copyAddress.ekstraInfo);
                  }}
                >
                  {copyAddress.title}
                </Button>
              ))}
            {showDetails && (
              <Button
                size="sm"
                variant="link"
                onClick={() => {
                  setIsResetDisabled((prev) => !prev);
                }}
              >
                {isResetDisabled ? 'Rediger adresse' : 'Avbryt'}
              </Button>
            )}
          </div>
        </div>
      )}
      {showDetails && (
        <div>
          <AddressDetails
            errors={errors}
            isFormDisabled={isResetDisabled}
            adresse={props.adresse}
            ekstraInfo={props.ekstraInfo}
            onEkstraInfoChanged={handleEkstraInfoChanged}
            coordinates={props.coordinates}
            setCoordinates={props.setCoordinates}
            onValueChanged={handleAddressValuesChanged}
            onCoordinatesChanged={props.onUpdateCoordinates}
            onCoordinatesRemoved={props.onDeleteCoordinates}
            onPostnrChanged={handlePostnrChanged}
          />
        </div>
      )}
    </div>
  );
};

export default AdresseInput;
